import React from "react";
import {
  Box,
  Text,
  VStack,
  Flex,
  Button,
  Icon,
} from "@chakra-ui/react";
import { BsFillInboxesFill } from "react-icons/bs";
import {
  SelectedSupplier,
  PartQuantity,
  getFulfilledQuantitiesByRootPart,
} from "./utils/partsCalculationUtils";
import { publicAxios as axios } from "../../config/axios";
import endpoints from "../../utils/endpoints";
import CboDetailsModal, { CboGroup } from "./CboDetailsModal";

interface PartsBreakdownPanelProps {
  selectedSuppliers: SelectedSupplier[];
  remainingParts: PartQuantity[];
  originalParts: PartQuantity[];
  reverseOrder?: boolean;
}

export const PartsBreakdownPanel: React.FC<PartsBreakdownPanelProps> = ({
  selectedSuppliers,
  remainingParts,
  originalParts,
  reverseOrder = false,
}) => {
  const [isCboModalOpen, setIsCboModalOpen] = React.useState(false);
  const [isCboLoading, setIsCboLoading] = React.useState(false);
  const [cboError, setCboError] = React.useState<string | null>(null);
  const [cboGroups, setCboGroups] = React.useState<CboGroup[]>([]);

  const fulfilledParts = React.useMemo(() => {
    const fulfilledMap = getFulfilledQuantitiesByRootPart(
      originalParts,
      selectedSuppliers,
    );

    return originalParts
      .map((part) => {
        const fulfilledQuantity = fulfilledMap.get(part.rootPartNumber) || 0;
        return {
          ...part,
          quantity: fulfilledQuantity,
        };
      })
      .filter((part) => part.quantity > 0);
  }, [selectedSuppliers, originalParts]);

  const partNameMap = React.useMemo(
    () =>
      new Map(
        remainingParts.map((part) => [part.partNumber, part.partName || ""]),
      ),
    [remainingParts],
  );

  const handleViewCbo = React.useCallback(async () => {
    const partNumbers = Array.from(
      new Set(
        remainingParts
          .map((part) => part.partNumber?.trim())
          .filter((partNumber): partNumber is string => Boolean(partNumber)),
      ),
    );

    if (partNumbers.length === 0) {
      setCboGroups([]);
      setCboError("No valid part numbers available");
      setIsCboModalOpen(true);
      return;
    }

    setIsCboModalOpen(true);
    setIsCboLoading(true);
    setCboError(null);

    try {
      const { data } = await axios.get(
        endpoints.getCboByPartNumbers(partNumbers),
        {
          headers: {
            Authorization: localStorage.getItem("auth_orca"),
          },
        },
      );

      const rawResults = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data?.results)
          ? data.data.results
          : [];
      const results: CboGroup[] = rawResults.filter(
        (group: any): group is CboGroup =>
          Boolean(group?.partNumber) && Array.isArray(group?.documents),
      );
      const resultByPartNumber = new Map(
        results.map((group: CboGroup) => [group.partNumber, group]),
      );
      const hydratedGroups: CboGroup[] = partNumbers.map((partNumber) => {
        const found = resultByPartNumber.get(partNumber);
        return found ?? { partNumber, documents: [] };
      });

      setCboGroups(hydratedGroups);
    } catch (error: any) {
      setCboGroups([]);
      setCboError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch CBO details",
      );
    } finally {
      setIsCboLoading(false);
    }
  }, [remainingParts]);

  return (
    <>
    <Flex
      wrap="wrap"
      gap={4}
      align="start"
      direction={reverseOrder ? "row-reverse" : "row"}
    >
      <Box
        flex="1 1 360px"
        minW="300px"
        bg="white"
        borderRadius="xl"
        p={4}
        w="100%"
        mt={4}
        boxShadow="md"
        fontFamily="'Nunito','Inter', sans-serif"
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Text
            fontSize="1rem"
            fontWeight="bold"
            color="gray.800"
            fontFamily="'Nunito','Inter', sans-serif"
          >
            Remaining Parts ({remainingParts.length})
          </Text>
          <Button
            size="sm"
            fontWeight="700"
            color="white"
            bgGradient="linear(to-r, #14966D, #1CB37D)"
            leftIcon={<Icon as={BsFillInboxesFill} boxSize={3.5} />}
            _hover={{ bgGradient: "linear(to-r, #107c5b, #17996b)" }}
            onClick={handleViewCbo}
            isDisabled={remainingParts.length === 0}
          >
            View CBO
          </Button>
        </Flex>
        {remainingParts.length > 0 ? (
          <Box
            maxH="220px"
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "transparent",
              },
              "&:hover": {
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            <VStack spacing={3} align="stretch">
              {remainingParts.map((part, index) => (
                <Box
                  key={`${part.rootPartNumber}-${index}`}
                  bg="#FEF7EE"
                  borderRadius="lg"
                  p={4}
                >
                  <Flex justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <Text
                        fontSize="0.875rem"
                        fontWeight="bold"
                        color="gray.800"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {part.partName || "Part Name Not Available"}
                      </Text>
                      <Text
                        fontSize="0.75rem"
                        color="gray.600"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {part.partNumber}
                      </Text>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text
                        fontSize="0.875rem"
                        fontWeight="bold"
                        color="#DF7B4C"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {part.quantity}
                      </Text>
                      <Text
                        fontSize="0.75rem"
                        color="gray.500"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        units needed
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
        ) : (
          <Text
            fontSize="0.875rem"
            color="green.600"
            fontWeight="medium"
            fontFamily="'Nunito','Inter', sans-serif"
          >
            All parts fulfilled! 🎉
          </Text>
        )}
      </Box>
      <Box
        flex="1 1 360px"
        minW="300px"
        bg="white"
        borderRadius="xl"
        p={4}
        w="100%"
        mt={4}
        boxShadow="md"
        fontFamily="'Nunito','Inter', sans-serif"
      >
        <Text
          fontSize="1rem"
          fontWeight="bold"
          color="gray.800"
          mb={4}
          fontFamily="'Nunito','Inter', sans-serif"
        >
          Fulfilled Parts ({fulfilledParts.length})
        </Text>
        {fulfilledParts.length > 0 ? (
          <Box
            maxH="220px"
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "transparent",
              },
              "&:hover": {
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            <VStack spacing={3} align="stretch">
              {fulfilledParts.map((part, index) => (
                <Box
                  key={`${part.rootPartNumber}-${index}`}
                  bg="#F3FDF5"
                  borderRadius="lg"
                  p={4}
                >
                  <Flex justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <Text
                        fontSize="0.875rem"
                        fontWeight="bold"
                        color="gray.800"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {part.partName || "Part Name Not Available"}
                      </Text>
                      <Text
                        fontSize="0.75rem"
                        color="gray.600"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {part.partNumber}
                      </Text>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text
                        fontSize="0.875rem"
                        fontWeight="bold"
                        color="#5EAB65"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {part.quantity}
                      </Text>
                      <Text
                        fontSize="0.75rem"
                        color="gray.500"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        units fulfilled
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
        ) : (
          <Text
            fontSize="0.875rem"
            color="gray.500"
            fontFamily="'Nunito','Inter', sans-serif"
          >
            No parts fulfilled yet
          </Text>
        )}
      </Box>
    </Flex>

    <CboDetailsModal
      isOpen={isCboModalOpen}
      onClose={() => setIsCboModalOpen(false)}
      isLoading={isCboLoading}
      error={cboError}
      cboGroups={cboGroups}
      partNameMap={partNameMap}
    />
    </>
  );
};
